import { useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"
import { OrgSwitcher } from "@/components/OrgSwitcher"
import { ProfileDropdown } from "@/components/layout/ProfileDropdown"
import { useUnreadSubmissionsCount } from "@/hooks/useFormSubmissions"
import { usePermission } from "@/hooks/usePermission"
import { useIsSponsor } from "@/hooks/useSponsorships"
import { SponsorCoBrand } from "@/components/dashboard/SponsorCoBrand"
export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const { user } = useAuthStore()
  const { currentOrg, userOrgs, fetchUserOrgs } = useOrgStore()
  const { hasPermission: canManageContacts } = usePermission("can_manage_contacts")
  const { hasPermission: canManageContent } = usePermission("can_manage_content")
  const { hasPermission: canManageEvents } = usePermission("can_manage_events")
  const { hasPermission: canManageMedia } = usePermission("can_manage_media")
  const { isSponsor } = useIsSponsor()
  const { count: unreadCount } = useUnreadSubmissionsCount()

  const isAdminRoute = pathname.startsWith("/admin")

  // Only fetch orgs when user is set (e.g. login or rehydration). Do NOT refetch on navigation.
  // fetchUserOrgs preserves currentOrg if the user has already switched during this session (see orgStore).
  useEffect(() => {
    if (user) {
      fetchUserOrgs()
    }
    // Intentionally depend only on user so we don't refetch on every layout re-render/navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold">NOS</h1>
              {!isAdminRoute && userOrgs && userOrgs.length > 1 && (
                <OrgSwitcher />
              )}
              {!isAdminRoute &&
                userOrgs &&
                userOrgs.length === 1 &&
                currentOrg?.name && (
                  <span className="text-sm text-gray-600">
                    {currentOrg.name}
                  </span>
                )}
              {!isAdminRoute && currentOrg?.id && <SponsorCoBrand orgId={currentOrg.id} />}
            </div>
            <div className="flex items-center gap-4">
              <ProfileDropdown />
            </div>
          </div>
        </div>
      </header>

      {!isAdminRoute && (
        <nav className="border-b bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="-mx-4 flex h-12 min-h-12 items-center gap-4 overflow-x-auto px-4 [-webkit-overflow-scrolling:touch] sm:mx-0 sm:gap-6 sm:px-0 md:gap-8">
              <Link
                to="/dashboard"
                className="text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                Dashboard
              </Link>
              <Link
                to="/dashboard/contacts"
                className="text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                Contacts
              </Link>
              {canManageContacts && (
                <Link
                  to="/dashboard/submissions"
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  Inbox
                  {unreadCount > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Link>
              )}
              <Link
                to="/dashboard/builder"
                className="text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                Website
              </Link>
              {canManageEvents && (
                <Link
                  to="/dashboard/events"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  Events
                </Link>
              )}
              {canManageContent && (
                <Link
                  to="/dashboard/blog"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  Blog
                </Link>
              )}
              {canManageContent && (
                <Link
                  to="/dashboard/campaigns"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  Campaigns
                </Link>
              )}
              {canManageMedia && (
                <Link
                  to="/dashboard/media"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  Media
                </Link>
              )}
              {isSponsor && (
                <Link
                  to="/sponsor"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  Sponsor console
                </Link>
              )}
              <Link
                to="/dashboard/team"
                className="text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                Team
              </Link>
              <Link
                to="/dashboard/settings"
                className="text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                Settings
              </Link>
            </div>
          </div>
        </nav>
      )}

      <main className="mx-auto min-w-0 max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  )
}
